import { useMemo, useState } from 'react';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import { listAuditLogs } from '../../api/auditLogs';
import { AuditAction, AuditLogEntry } from '../../types/auditLog';
import { TableToolbar } from '../../components/table/TableToolbar';
import { PageHeader } from '../../components/PageHeader';

const AUDIT_ACTIONS: AuditAction[] = [
  'CREATED',
  'EDITED',
  'DELETED',
  'ASSIGNED',
  'TRANSFERRED',
  'STATUS_CHANGED',
  'DOCUMENT_UPLOADED',
  'DOCUMENT_APPROVED',
  'PAYMENT_ADDED',
  'VISA_UPDATED',
  'PASSWORD_CHANGED',
  'PASSWORD_RESET',
  'PASSWORD_RESET_REQUESTED',
  'PASSWORD_RESET_COMPLETED',
];

const statusColor = (status: number | null): 'success' | 'warning' | 'error' | 'default' => {
  if (status === null) return 'default';
  if (status >= 500) return 'error';
  if (status >= 400) return 'warning';
  if (status >= 200) return 'success';
  return 'default';
};

export const AuditLogsPage = () => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [resource, setResource] = useState('');
  const [method, setMethod] = useState('');
  const [action, setAction] = useState<AuditAction | ''>('');
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null);

  const auditLogsQuery = useQuery({
    queryKey: ['audit-logs', paginationModel.page, paginationModel.pageSize, resource, method, action],
    queryFn: () =>
      listAuditLogs({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        resource: resource || undefined,
        method: method || undefined,
        action: action || undefined,
      }),
    placeholderData: (prev) => prev,
  });

  const columns: GridColDef<AuditLogEntry>[] = useMemo(
    () => [
      {
        field: 'createdAt',
        headerName: 'When',
        width: 170,
        valueGetter: (_v, row) => new Date(row.createdAt).toLocaleString(),
      },
      {
        field: 'user',
        headerName: 'User',
        flex: 1,
        valueGetter: (_v, row) => (row.user ? `${row.user.firstName} ${row.user.lastName}` : 'Unauthenticated'),
      },
      { field: 'method', headerName: 'Method', width: 90, valueGetter: (_v, row) => row.method ?? '—' },
      { field: 'path', headerName: 'Path', flex: 1.5, valueGetter: (_v, row) => row.path ?? '—' },
      { field: 'resource', headerName: 'Resource', width: 130, valueGetter: (_v, row) => row.resource ?? '—' },
      { field: 'action', headerName: 'Action', width: 140, valueGetter: (_v, row) => row.action ?? '—' },
      {
        field: 'statusCode',
        headerName: 'Status',
        width: 100,
        renderCell: (params) =>
          params.value === null ? '—' : <Chip size="small" label={params.value} color={statusColor(params.value)} />,
      },
      { field: 'ipAddress', headerName: 'IP', width: 130, valueGetter: (_v, row) => row.ipAddress ?? '—' },
    ],
    [],
  );

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={HistoryOutlinedIcon}
        color="#667085"
        title="Audit Logs"
        subtitle="Full API audit trail for compliance and troubleshooting."
      />

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Resource"
          placeholder="e.g. leads"
          sx={{ width: 200 }}
          value={resource}
          onChange={(e) => setResource(e.target.value)}
        />
        <TextField
          select
          size="small"
          label="Method"
          sx={{ width: 160 }}
          value={method}
          onChange={(e) => setMethod(e.target.value)}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          <MenuItem value="POST">POST</MenuItem>
          <MenuItem value="PATCH">PATCH</MenuItem>
          <MenuItem value="PUT">PUT</MenuItem>
          <MenuItem value="DELETE">DELETE</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Action"
          sx={{ width: 200 }}
          value={action}
          onChange={(e) => setAction(e.target.value as AuditAction | '')}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          {AUDIT_ACTIONS.map((a) => (
            <MenuItem key={a} value={a}>
              {a}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Box sx={{ height: 600, bgcolor: 'background.paper', borderRadius: 1 }}>
        <DataGrid
          rows={auditLogsQuery.data?.data ?? []}
          columns={columns}
          rowCount={auditLogsQuery.data?.meta.total ?? 0}
          loading={auditLogsQuery.isFetching}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100]}
          slots={{ toolbar: TableToolbar }}
          disableRowSelectionOnClick
          onRowClick={(params) => setSelectedEntry(params.row)}
        />
      </Box>

      <Dialog open={!!selectedEntry} onClose={() => setSelectedEntry(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Audit log entry</DialogTitle>
        <DialogContent>
          {selectedEntry && (
            <Stack spacing={1}>
              <Typography variant="body2">
                {selectedEntry.action ? (
                  <strong>{selectedEntry.action}</strong>
                ) : (
                  <>
                    <strong>{selectedEntry.method}</strong> {selectedEntry.path}
                  </>
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {new Date(selectedEntry.createdAt).toLocaleString()}
                {selectedEntry.statusCode !== null && ` · status ${selectedEntry.statusCode}`} · IP{' '}
                {selectedEntry.ipAddress ?? '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                User:{' '}
                {selectedEntry.user
                  ? `${selectedEntry.user.firstName} ${selectedEntry.user.lastName} (${selectedEntry.user.email})`
                  : 'Unauthenticated'}
              </Typography>
              {(selectedEntry.oldValue || selectedEntry.newValue) && (
                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">Old value</Typography>
                    <Box
                      component="pre"
                      sx={{ bgcolor: 'action.hover', p: 1.5, borderRadius: 1, fontSize: 13, overflowX: 'auto' }}
                    >
                      {selectedEntry.oldValue ? JSON.stringify(selectedEntry.oldValue, null, 2) : '(empty)'}
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">New value</Typography>
                    <Box
                      component="pre"
                      sx={{ bgcolor: 'action.hover', p: 1.5, borderRadius: 1, fontSize: 13, overflowX: 'auto' }}
                    >
                      {selectedEntry.newValue ? JSON.stringify(selectedEntry.newValue, null, 2) : '(empty)'}
                    </Box>
                  </Box>
                </Stack>
              )}
              {selectedEntry.requestBody && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    Request body
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      bgcolor: 'action.hover',
                      p: 1.5,
                      borderRadius: 1,
                      fontSize: 13,
                      overflowX: 'auto',
                      maxHeight: 300,
                    }}
                  >
                    {JSON.stringify(selectedEntry.requestBody, null, 2)}
                  </Box>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
